import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { PhysicalChartCardShared } from '@/features/patient-record/components/DemographicsSection/PhysicalChartCardShared';
import { useFamilyDetailMutations } from '@/features/family-record/hooks/useFamilyDetailMutations';
import type { PhysicalChartDto } from '@/features/families/api';
import type { PhysicalChartViewModel } from '@/models/patient';
import type { FamilyViewModel } from '@/models/family';

interface PhysicalChartCardProps {
  readonly family: FamilyViewModel;
  /** Default the location picker to "Other" when unset (pedigree panel). */
  readonly defaultLocationOther?: boolean;
}

/**
 * Family (FAMILY) physical-chart card — a thin wrapper over the shared
 * {@link PhysicalChartCardShared}. The `PhysicalChart` entity is polymorphic on
 * the BE, so the family chart round-trips date + location + staff + notes via
 * `PATCH /families/{guid}` (full-block replace; `null` clears).
 */
export function PhysicalChartCard({ family, defaultLocationOther = false }: PhysicalChartCardProps): ReactElement {
  const { t } = useTranslation();
  const { patch } = useFamilyDetailMutations(family.guid);

  return (
    <PhysicalChartCardShared
      parentType="FAMILY"
      title={t('routes.family.physicalChart.heading')}
      chart={family.physicalChart}
      saving={patch.isPending}
      defaultLocationOther={defaultLocationOther}
      onSave={(next) => {
        patch.mutate({ physical_chart: toPhysicalChartDto(next) });
      }}
    />
  );
}

function toPhysicalChartDto(vm: PhysicalChartViewModel | null): PhysicalChartDto | null {
  if (vm === null) return null;
  return {
    ...(vm.id !== null ? { id: vm.id } : {}),
    date: vm.date,
    location: vm.location,
    staff_member_user_guid: vm.staffMemberUserGuid,
    notes: vm.notes,
  };
}
